import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, ASSET_URL } from "../lib/api";
import { useSite } from "../lib/site-context";
import { applySeo } from "../lib/seo";

export default function NewsIndexPage() {
  const { lang, t, settings } = useSite();
  const [items, setItems] = React.useState([]);
  const [media, setMedia] = React.useState([]);

  React.useEffect(() => {
    const rawBase = settings?.public_base_url || "https://dr-b-digitec.com";
    const baseUrl = rawBase.replace(/\/$/, "");
    applySeo({
      slug: "wissen/news",
      lang,
      siteName: settings?.site_name || "Dr. B.-Digitec",
      baseUrl,
      seo: {
        title: lang === "en"
          ? "News & Articles on Digital Dentistry – Dr. B.-Digitec"
          : "Aktuelles & Fachbeiträge zu digitalem Zahnersatz – Dr. B.-Digitec",
        description: lang === "en"
          ? "Current articles and insights on digital dentures, intraoral scanning, scan data checks, CAD/CAM and digital workflows for dental practices."
          : "Aktuelle Fachbeiträge, Praxiswissen und News zu digitalem Zahnersatz, Intraoralscan, Scandatenprüfung, CAD/CAM und digitalen Workflows für Zahnarztpraxen.",
        robots: "index,follow"
      }
    });
  }, [lang, settings]);


  React.useEffect(() => {
    Promise.all([
      api.news().catch(() => []),
      api.media().catch(() => [])
    ]).then(([news, mediaAssets]) => {
      setItems((news || []).filter(n => n.published !== false));
      setMedia(mediaAssets || []);
    });
  }, []);

  const mediaMap = React.useMemo(
    () => Object.fromEntries((media || []).map(m => [m.id, m])),
    [media]
  );

  const imageFor = (n) => {
    const m = n.cover_media_id ? mediaMap[n.cover_media_id] : null;
    const path = m?.variants?.webp || m?.variants?.thumb || m?.variants?.original;
    return path ? ASSET_URL(path) : null;
  };

  return (
    <main className="pt-36 md:pt-44 pb-24" data-testid="news-index">
      <section className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-3">
            <div className="brut-tag">{t("Wissen / News", "Knowledge / News")}</div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h1 className="brut-h1 mb-6">{t("Fachbeiträge für digitale Praxen.", "Articles for digital practices.")}</h1>
            <p className="brut-body max-w-3xl opacity-80">
              {t("Aktuelles zu digitalem Zahnersatz, Scandaten, CAD/CAM und digitalen Workflows für Zahnarztpraxen.", "Insights on digital dentistry, scan data, CAD/CAM and digital workflows.")}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {items.map((n) => {
            const title = lang === "en" ? (n.title_en || n.title_de) : n.title_de;
            const excerpt = lang === "en" ? (n.excerpt_en || n.excerpt_de) : n.excerpt_de;
            const img = imageFor(n);

            return (
              <Link key={n.id} to={`/wissen/news/${n.slug}`} className="news-index-card group block bg-[var(--ink)] text-[var(--bg)] rounded-[28px] shadow-xl overflow-hidden hover:-translate-y-1 transition-all">
                {img && <div className="news-card-image"><img src={img} alt={title} loading="lazy" /></div>}
                <div className="news-index-card-body p-7 md:p-8 flex flex-col">
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] opacity-55 mb-5">{(n.published_at || "").slice(0, 10)}</div>
                  <h2 className="text-2xl md:text-3xl font-black leading-[0.98] tracking-[-0.04em] mb-5">{title}</h2>
                  {excerpt && <p className="brut-body opacity-75 mb-6">{excerpt}</p>}
                  <span className="inline-flex items-center gap-1 mt-auto font-mono text-[11px] uppercase tracking-wider">
                    {t("Weiterlesen", "Read more")} <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}
